import { getToken, clearToken } from '../utils/authToken';
/**
 * Modal de Re-Aceptación de Términos (VTC Module)
 * Se muestra cuando el usuario tiene una versión de términos desactualizada
 * El usuario no puede usar la plataforma hasta aceptar la nueva versión
 */

import { useState, useEffect } from 'react';
import { API_URL, API } from '../config';
import { createPortal } from 'react-dom';
import { FileText, Shield, AlertTriangle, CheckCircle, Loader2, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';


// Default terms text (same as in AuthPage.jsx)
const DEFAULT_TERMS_TEXT = `CLÁUSULA DE SANCIONES Y CONDUCTA DEL USUARIO

Al crear una cuenta en DamyxIA, el usuario acepta cumplir con las siguientes normas de conducta y reconoce que su incumplimiento puede resultar en las sanciones descritas a continuación:

1. NORMAS DE CONDUCTA

El usuario se compromete a:
• Utilizar la plataforma de manera responsable y ética
• No compartir contenido ofensivo, discriminatorio o ilegal
• No intentar vulnerar la seguridad de la plataforma
• No utilizar el servicio para actividades ilegales o fraudulentas
• Respetar los derechos de propiedad intelectual de terceros
• No acosar, intimidar ni difamar a otros usuarios
• No realizar spam ni publicidad no autorizada

2. SISTEMA DE SANCIONES PROGRESIVAS

2.1 Primera Infracción: ADVERTENCIA
• El usuario recibirá una notificación formal de advertencia
• Se registrará la infracción en el historial de la cuenta
• El usuario deberá reconocer la advertencia para continuar usando el servicio

2.2 Segunda Infracción: SUSPENSIÓN TEMPORAL
• Suspensión de la cuenta por un período de 7 días
• Durante la suspensión, el usuario no podrá acceder a ninguna funcionalidad
• Al finalizar la suspensión, se requerirá aceptar nuevamente los términos

2.3 Tercera Infracción: SUSPENSIÓN EXTENDIDA
• Suspensión de la cuenta por un período de 30 días
• Revisión obligatoria del caso por el equipo de moderación
• Posible restricción permanente de ciertas funcionalidades

2.4 Infracciones Graves o Reincidencia: ELIMINACIÓN PERMANENTE
• Eliminación definitiva de la cuenta sin posibilidad de recuperación
• Pérdida de todos los datos, conversaciones y configuraciones
• Prohibición de crear nuevas cuentas en la plataforma

3. INFRACCIONES GRAVES (SANCIÓN INMEDIATA)

Las siguientes conductas resultarán en eliminación inmediata de la cuenta:
• Actividades ilegales o promoción de las mismas
• Acoso grave o amenazas a otros usuarios
• Intentos de hackeo o vulneración de seguridad
• Distribución de malware o contenido malicioso
• Suplantación de identidad
• Explotación de menores en cualquier forma

4. DERECHO DE APELACIÓN

El usuario tiene derecho a:
• Solicitar revisión de cualquier sanción en un plazo de 15 días
• Presentar evidencia en su defensa
• Recibir respuesta en un plazo máximo de 7 días hábiles

5. ACEPTACIÓN

Al marcar la casilla de aceptación, el usuario confirma que:
• Ha leído y comprendido estas normas en su totalidad
• Acepta cumplir con todas las normas de conducta establecidas
• Reconoce y acepta el sistema de sanciones progresivas
• Entiende que el incumplimiento puede resultar en la pérdida de acceso

Última actualización: Diciembre 2025`;

const TermsReacceptModal = ({ 
  isOpen, 
  onAccept, 
  onLogout, 
  currentVersion = 1,
  userEmail = ''
}) => {
  const { language } = useLanguage();
  const es = language === 'es';
  const [isLoading, setIsLoading] = useState(false);
  const [customTermsText, setCustomTermsText] = useState(null);
  const [loadingText, setLoadingText] = useState(true);

  // Load custom terms text if available
  useEffect(() => {
    const fetchTermsText = async () => {
      try {
        setLoadingText(true);
        const token = getToken();
        const response = await axios.get(`${API}/admin/terms/text`, {
        });
        if (response.data.has_custom_text) {
          setCustomTermsText(response.data.text);
        }
      } catch (error) {
        // If user is not admin or endpoint fails, use default text
      } finally {
        setLoadingText(false);
      }
    };

    if (isOpen) {
      fetchTermsText();
    }
  }, [isOpen]);

  const handleAccept = async () => {
    setIsLoading(true);
    try {
      const token = getToken();
      await axios.post(`${API}/auth/accept-terms`, {}, {
      });
      
      toast.success(es ? 'Términos aceptados exitosamente' : 'Terms accepted successfully');
      onAccept();
    } catch (error) {
      console.error('Error accepting terms:', error);
      toast.error(es ? 'Error al aceptar los términos. Intenta de nuevo.' : 'Error accepting terms. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    clearToken();
    localStorage.removeItem('userEmail');
    localStorage.removeItem('isVerified');
    onLogout();
  };

  if (!isOpen) return null;

  const termsText = customTermsText || DEFAULT_TERMS_TEXT;

  const modalContent = (
    <div 
      data-testid="terms-reaccept-modal"
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md"
    >
      <div className="relative w-full max-w-3xl max-h-[90vh] bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        {/* Header with warning */}
        <div className="bg-amber-500/10 border-b border-amber-500/30 p-4">
          <div className="flex items-center gap-3 text-amber-400">
            <AlertTriangle className="w-6 h-6 flex-shrink-0" />
            <div>
              <h2 className="text-lg font-semibold">{es ? 'Actualización de Términos y Condiciones' : 'Terms and Conditions Update'}</h2>
              <p className="text-sm text-amber-300/80">
                Hemos actualizado nuestras Normas de Conducta. Debes aceptar la nueva versión para continuar usando DamyxIA.
              </p>
            </div>
          </div>
        </div>

        {/* Modal Title */}
        <div className="flex items-center justify-between p-4 border-b border-zinc-700">
          <div className="flex items-center gap-3">
            <Shield className="w-5 h-5 text-indigo-400" />
            <div>
              <h3 className="text-white font-medium">{es ? 'Normas de Conducta y Sistema de Sanciones' : 'Code of Conduct and Sanctions System'}</h3>
              <p className="text-sm text-zinc-400">
                Versión {currentVersion} • Usuario: {userEmail}
              </p>
            </div>
          </div>
          <span className="px-3 py-1 text-xs font-medium bg-indigo-500/20 text-indigo-400 rounded-full">
            v{currentVersion}
          </span>
        </div>

        {/* Terms Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loadingText ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-indigo-400 animate-spin" />
            </div>
          ) : (
            <div className="prose prose-invert prose-sm max-w-none">
              <pre className="whitespace-pre-wrap text-zinc-300 text-sm leading-relaxed font-sans bg-transparent p-0 m-0">
                {termsText}
              </pre>
            </div>
          )}
        </div>

        {/* Footer with actions */}
        <div className="p-4 border-t border-zinc-700 bg-zinc-800/50">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-sm text-zinc-400">
              <FileText className="w-4 h-4" />
              <span>Al aceptar, confirmas que has leído y comprendido estas normas.</span>
            </div>
            <div className="flex gap-3 w-full sm:w-auto">
              <button
                data-testid="terms-logout-btn"
                onClick={handleLogout}
                disabled={isLoading}
                className="flex-1 sm:flex-none px-4 py-2.5 text-sm text-zinc-400 hover:text-white hover:bg-zinc-700 rounded-lg transition-colors disabled:opacity-50"
              >
                Cerrar Sesión
              </button>
              <button
                data-testid="terms-accept-btn"
                onClick={handleAccept}
                disabled={isLoading || loadingText}
                className="flex-1 sm:flex-none px-6 py-2.5 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Procesando...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    {es ? 'Acepto los Nuevos Términos' : 'I Accept the New Terms'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default TermsReacceptModal;
